import {useCallback, useEffect, useMemo, useState} from "react";
import {Navigate, useOutletContext} from "react-router-dom";
import {message, Table} from "antd";
import {useTranslation} from "react-i18next";
import {APIv1} from "../../api";
import {extractDateBySecond, getStoredUser} from "../../utils";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";
import useUrlTableState from "../../hooks/useUrlTableState";
import MarkerMobileColumns from "./markermobile.constants";
import "./MarkerMobile.scss";

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 100;

const MARKER_MOBILE_FILTER_PARAM_MAP = {
    is_active: "is_active",
};

const MARKER_MOBILE_FILTER_VALUE_PARSERS = {
    is_active: (value) => value === "true",
};

const formatValue = (value) => value ?? "-";

const normalizeLicense = (license) => ({
    key: license.id,
    id: license.id,
    license_device_id: formatValue(license.license_device_id),
    company_inn: formatValue(license.company_inn),
    company_name: formatValue(license.company_name),
    device_serial_number: formatValue(license.device_serial_number),
    is_active: license.is_active,
    created_date: extractDateBySecond(license.created_date),
    updated_date: extractDateBySecond(license.updated_date),
});

const MarkerMobile = () => {
    const {t} = useTranslation();
    const {searchText} = useOutletContext();
    const [userData] = useState(() => getStoredUser() || {});
    const [licenses, setLicenses] = useState([]);
    const [totalLicenses, setTotalLicenses] = useState(0);
    const [loading, setLoading] = useState(true);
    const [updatingStatusIds, setUpdatingStatusIds] = useState([]);
    const isStaff = Boolean(userData?.data?.is_admin);
    const filterLabels = useMemo(() => ({
        is_active: t("pages.markerMobile.listColumns.status"),
    }), [t]);

    const tableState = useUrlTableState({
        defaultPageSize: DEFAULT_PAGE_SIZE,
        filterParamMap: MARKER_MOBILE_FILTER_PARAM_MAP,
        filterLabels,
        filterValueParsers: MARKER_MOBILE_FILTER_VALUE_PARSERS,
        searchText,
    });
    const pageSize = Math.min(tableState.pageSize, MAX_PAGE_SIZE);

    const fetchLicenses = useCallback(async () => {
        if (!userData.token || !isStaff) {
            return;
        }

        setLoading(true);

        try {
            const response = await APIv1.get("/device/license/list/", {
                params: {
                    page: tableState.currentPage,
                    page_size: pageSize,
                    search: tableState.search,
                    ordering: tableState.ordering,
                    ...tableState.filters,
                },
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });
            const results = response.data?.results ?? response.data ?? [];
            const data = Array.isArray(results) ? results.map(normalizeLicense) : [];

            setLicenses(data);
            setTotalLicenses(response.data?.count ?? data.length);
        } catch (error) {
            console.error("Error fetching marker mobile licenses:", error);
            setLicenses([]);
            setTotalLicenses(0);
        } finally {
            setLoading(false);
        }
    }, [isStaff, pageSize, tableState.currentPage, tableState.filters, tableState.ordering, tableState.search, userData.token]);

    useEffect(() => {
        fetchLicenses();
    }, [fetchLicenses]);

    const handleStatusChange = useCallback(async (record, isActive) => {
        if (!userData.token || updatingStatusIds.includes(record.id)) {
            return;
        }

        setUpdatingStatusIds((currentIds) => [...currentIds, record.id]);
        setLicenses((currentLicenses) => currentLicenses.map((license) => (
            license.id === record.id ? {...license, is_active: isActive} : license
        )));

        try {
            await APIv1.patch(`/device/license/update/${record.id}/`, {
                is_active: isActive,
            }, {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });
            message.success(t("pages.markerMobile.messages.statusUpdateSuccess"));
        } catch (error) {
            console.error("Error updating marker mobile status:", error);
            setLicenses((currentLicenses) => currentLicenses.map((license) => (
                license.id === record.id ? {...license, is_active: record.is_active} : license
            )));
            message.error(t("pages.markerMobile.messages.statusUpdateError"));
        } finally {
            setUpdatingStatusIds((currentIds) => currentIds.filter((item) => item !== record.id));
        }
    }, [t, updatingStatusIds, userData.token]);

    const columns = useMemo(
        () => MarkerMobileColumns(t, handleStatusChange, updatingStatusIds),
        [handleStatusChange, t, updatingStatusIds],
    );

    const controlledColumns = useMemo(
        () => tableState.controlledColumns(columns),
        [columns, tableState],
    );

    if (userData.token && !isStaff) {
        return <Navigate to="/analysis" replace/>;
    }

    const tableChangeHandler = tableState.handleTableChange(columns);

    return (
        <div className="content_container marker-mobile-list">
            <ListStateIndicator
                items={tableState.stateIndicators}
                onReset={tableState.resetState}
                onRemove={tableState.removeState}
            />
            <Table
                bordered
                columns={controlledColumns}
                dataSource={licenses}
                loading={loading}
                onChange={tableChangeHandler}
                pagination={{
                    total: totalLicenses,
                    current: tableState.currentPage,
                    pageSize,
                    defaultPageSize: DEFAULT_PAGE_SIZE,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                    pageSizeOptions: ["50", "100"],
                }}
            />
        </div>
    );
};

export default MarkerMobile;
